import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  FaArrowRight, 
  FaAward, 
  FaChartLine, 
  FaCheckCircle, 
  FaCog, 
  FaGlobe, 
  FaRocket, 
  FaStar,
  FaTrophy,
  FaUsers,
  FaShieldAlt
} from 'react-icons/fa';
import SEO from '../components/SEO/SEO';
import contentData from '../data/contentData.json';

const CaseStudyCard = ({ title, company, category, description, results, image, index }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div 
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group relative overflow-hidden"
    >
      {/* Background Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-all duration-700 blur-xl"></div>
      
      <div className="relative bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden h-full">
        {/* Image Section with Enhanced Effects */}
        <div className="relative overflow-hidden aspect-[4/3] bg-gradient-to-br from-blue-100 to-purple-100">
          {image ? (
            <>
              <img 
                src={image} 
                alt={title} 
                className="object-cover w-full h-full transition-all duration-700 group-hover:scale-110" 
              />
              {/* Image Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400 bg-gradient-to-br from-gray-100 to-gray-200">
              <FaGlobe className="text-4xl" />
            </div>
          )}
          
          {/* Floating Category Badge */}
          <div className="absolute top-4 left-4">
            <motion.div
              className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-full shadow-lg backdrop-blur-sm"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              {category}
            </motion.div>
          </div>

          {/* Success Badge */}
          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-500">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
              <FaTrophy className="text-white text-xl" />
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-8">
          {/* Company Badge */}
          <motion.div
            className="inline-flex items-center px-4 py-2 mb-4 text-sm font-medium text-blue-700 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200/50 rounded-xl"
            whileHover={{ scale: 1.02 }}
          >
            <FaUsers className="mr-2 text-blue-600" />
            {company}
          </motion.div>

          <h3 className="mb-4 text-2xl font-bold text-gray-900 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300 leading-tight">
            {title}
          </h3>
          
          <p className="mb-6 text-gray-600 leading-relaxed line-clamp-3">
            {description}
          </p>

          {/* Results Section with Enhanced Design */}
          <div className="mb-8 p-6 bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl border border-green-100/50">
            <h4 className="flex items-center mb-4 font-semibold text-green-800">
              <FaChartLine className="mr-2 text-green-600" />
              Key Results
            </h4>
            <ul className="space-y-3">
              {results.map((result, resultIndex) => (
                <motion.li 
                  key={resultIndex}
                  className="flex items-start text-gray-700"
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  transition={{ duration: 0.4, delay: 0.5 + resultIndex * 0.1 }}
                >
                  <FaCheckCircle className="mr-3 mt-0.5 text-green-500 flex-shrink-0" />
                  <span className="leading-relaxed">{result}</span>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Enhanced CTA Button */}
          <Link 
            to="#" 
            className="group/btn inline-flex items-center w-full justify-center px-6 py-4 font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            <span>Read Full Case Study</span>
            <FaArrowRight className="ml-2 transform group-hover/btn:translate-x-1 transition-transform duration-200" />
          </Link>
        </div>

        {/* Decorative Elements */}
        <div className="absolute -bottom-2 -right-2 w-24 h-24 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
        <div className="absolute -top-2 -left-2 w-16 h-16 bg-gradient-to-br from-purple-400/10 to-blue-400/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      </div>
    </motion.div>
  );
};

const FilterButton = ({ category, isActive, onClick, index }) => {
  return (
    <motion.button
      className={`relative px-8 py-4 font-semibold rounded-2xl transition-all duration-300 ${
        isActive
          ? 'text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg transform scale-105'
          : 'text-gray-700 bg-white/80 backdrop-blur-sm border border-gray-200/50 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:border-blue-200/50 hover:scale-102 shadow-sm'
      }`}
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.95 }}
    >
      {/* Glow effect for active button */}
      {isActive && (
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur-lg opacity-30"></div>
      )}
      <span className="relative z-10">{category}</span>
    </motion.button>
  );
};

const CaseStudiesPage = () => {
  const categories = ["All", "Finance", "Healthcare", "E-commerce", "Manufacturing", "Technology"];
  const [activeCategory, setActiveCategory] = useState("All");
  const [isLoading, setIsLoading] = useState(true);

  // SEO data for Case Studies page
  const seoData = {
    title: "Case Studies - Success Stories",
    description: `Explore real-world success stories and case studies from ${contentData.company.name}. See how we've helped businesses achieve cloud transformation, cost savings, and improved performance with our expert cloud services.`,
    keywords: "Case Studies, Cloud Success Stories, AWS Migration Case Studies, DevOps Implementation, Cloud Transformation Results, Client Success Stories"
  };

  useEffect(() => {
    // Simulate loading delay
    setTimeout(() => {
      setIsLoading(false);
    }, 800);
  }, []);

  // Enhanced case studies data
  const caseStudies = [
    {
      title: "Enterprise Cloud Migration",
      company: "Global Financial Services",
      category: "Finance",
      description: "How we helped a leading financial institution migrate their legacy systems to a secure cloud environment with zero downtime and enhanced security protocols.",
      results: [
        "35% reduction in operational costs",
        "99.99% uptime achieved",
        "50% faster application deployment",
        "Enhanced security compliance"
      ],
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
    },
    {
      "title": "Fiber Optics Mapping & Management",
      "company": "NavyaTechs",
      "category": "Telecom / Infrastructure",
      "description": "We helped NavyaTechs digitize and manage their entire fiber optics infrastructure using Google Maps API integration and a centralized admin dashboard.",
      "results": [
        "Mapped 500+ km of fiber lines with GPS accuracy",
        "Reduced maintenance request time by 70%",
        "Integrated fault reports with real-time map markers",
        "One-click access to route-wise maintenance history"
      ],
      "image": "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
    },
    {
      "title": "Credit Management App for Communities",
      "company": "Jamattu",
      "category": "Fintech / Community Credit",
      "description": "Developed a secure credit and lending tracking app for local community groups, allowing seamless tracking of credit transactions, payment cycles, and member balances.",
      "results": [
        "Managed ₹5Cr+ in community credit transactions",
        "Launched Android app with multi-role access in 45 days",
        "Automated reminders reduced defaults by 32%",
        "Real-time reports for treasurers and admins"
      ],
      "image": "https://images.unsplash.com/photo-1591696205602-2f950c417cb9?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
    },
    {
      "title": "Food Delivery Platform Modernization",
      "company": "Hellodish",
      "category": "Food Tech",
      "description": "We partnered with Hellodish, a Zomato-style food delivery startup in Anjar, Gujarat, to build a scalable, real-time ordering system with cloud-native architecture.",
      "results": [
        "Launched across 3 cities in 6 months",
        "Integrated 100+ restaurants via vendor dashboard",
        "Reduced app crashes by 80%",
        "Enabled 24x7 auto-scaling with AWS Fargate"
      ],
      "image": "https://images.unsplash.com/photo-1600891964599-f61ba0e24092?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
    },
   
    {
      title: "DevOps Implementation",
      company: "HealthTech Solutions",
      category: "Healthcare",
      description: "Implementing comprehensive CI/CD pipelines and DevOps practices for a healthcare provider to improve deployment efficiency and patient data security.",
      results: [
        "300% improvement in deployment frequency",
        "90% reduction in deployment failures",
        "75% faster time to market for new features",
        "HIPAA compliance achieved"
      ],
      image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
    },
    {
      title: "Cloud Security Overhaul",
      company: "ShopEasy",
      category: "E-commerce",
      description: "Comprehensive security assessment and implementation for a major e-commerce platform handling millions of transactions daily.",
      results: [
        "Achieved PCI DSS compliance",
        "95% reduction in security vulnerabilities",
        "Zero data breaches since implementation",
        "Enhanced customer trust and retention"
      ],
      image: "https://images.unsplash.com/photo-1535191042502-e6a9a3d407e7?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
    },
    {
      title: "Multi-Cloud Strategy",
      company: "IndustrialTech",
      category: "Manufacturing",
      description: "Developing and implementing a comprehensive multi-cloud strategy for a manufacturing company to optimize costs and improve resilience.",
      results: [
        "40% cost optimization across cloud platforms",
        "Improved disaster recovery capabilities",
        "Enhanced business continuity",
        "99.9% system availability"
      ],
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
    },
    {
      title: "Kubernetes Migration",
      company: "SaaS Provider",
      category: "Technology",
      description: "Migrating a monolithic application to microservices architecture using Kubernetes for improved scalability and performance.",
      results: [
        "60% improvement in application performance",
        "85% increase in scalability",
        "Reduced infrastructure costs by 45%",
        "Auto-scaling capabilities implemented"
      ],
      image: "https://images.unsplash.com/photo-1597733336794-12d05021d510?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
    },
    {
      title: "Data Analytics Platform",
      company: "Retail Chain",
      category: "E-commerce",
      description: "Building a comprehensive cloud-based data analytics platform for a retail chain to improve decision-making and customer insights.",
      results: [
        "360° view of customer behavior",
        "25% increase in sales through targeted campaigns",
        "Reduced inventory costs by 30%",
        "Real-time analytics dashboard"
      ],
      image: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
    }
  ];

  const filteredCaseStudies = activeCategory === "All" 
    ? caseStudies 
    : caseStudies.filter(study => study.category === activeCategory);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <>
      <SEO {...seoData} />
      
      {/* Enhanced Hero Section */}
      <section className="relative py-32 md:py-40 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />
          <motion.div 
            className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.4, 0.7, 0.4]
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2
            }}
          />
          <motion.div 
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-full blur-3xl"
            animate={{ rotate: 360 }}
            transition={{ duration: 50, repeat: Infinity, ease: 'linear' }}
          />
        </div>

        <div className="container relative z-10">
          <motion.div 
            className="max-w-5xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Enhanced Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-8 py-4 mb-8"
            >
              <FaAward className="text-blue-400 mr-3 text-xl" />
              <span className="text-white font-semibold text-lg">Success Stories</span>
            </motion.div>

            <h1 className="mb-8 text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
              <span className="block mb-4">Transformative</span>
              <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Case Studies
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed mb-12">
              Discover how our innovative cloud solutions have empowered businesses across industries 
              to overcome challenges, optimize operations, and achieve remarkable growth.
            </p>

            {/* Enhanced Stats Grid */}
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {[
                { number: "50+", label: "Success Stories", icon: FaTrophy },
                { number: "95%", label: "Client Satisfaction", icon: FaStar },
                { number: "40%", label: "Average Cost Savings", icon: FaChartLine },
                { number: "99.9%", label: "Uptime Achieved", icon: FaShieldAlt }
              ].map((stat, index) => (
                <motion.div 
                  key={index}
                  className="text-center group"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <stat.icon className="text-white text-2xl" />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-blue-200">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Enhanced Filter Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        <div className="container">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Filter by Industry
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
          </motion.div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            {categories.map((category, index) => (
              <FilterButton
                key={index}
                category={category}
                isActive={activeCategory === category}
                onClick={() => setActiveCategory(category)}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Case Studies Grid */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        <div className="container">
          {isLoading ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <div key={item} className="rounded-3xl bg-gradient-to-br from-gray-200 to-gray-300 h-[600px] animate-pulse">
                  <div className="w-full h-48 bg-gray-300 rounded-t-3xl mb-4"></div>
                  <div className="p-8 space-y-4">
                    <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                    <div className="h-6 bg-gray-300 rounded w-3/4"></div>
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-300 rounded"></div>
                      <div className="h-3 bg-gray-300 rounded w-5/6"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <motion.div 
              className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {filteredCaseStudies.map((study, index) => (
                <CaseStudyCard
                  key={index}
                  title={study.title}
                  company={study.company}
                  category={study.category}
                  description={study.description}
                  results={study.results}
                  image={study.image}
                  index={index}
                />
              ))}
            </motion.div>
          )}
          
          {filteredCaseStudies.length === 0 && !isLoading && (
            <motion.div 
              className="p-16 text-center bg-white/80 backdrop-blur-sm rounded-3xl border border-gray-200/50"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <FaGlobe className="text-3xl text-gray-400" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-700 mb-2">No case studies found</h3>
              <p className="text-gray-500">Try selecting a different category to see more success stories</p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="relative py-32 overflow-hidden">
        {/* Background with Advanced Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900"></div>
          <motion.div 
            className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.3),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.3),transparent_50%)]"
            animate={{
              background: [
                'radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.3),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.3),transparent_50%)',
                'radial-gradient(circle_at_70%_30%,rgba(59,130,246,0.3),transparent_50%),radial-gradient(circle_at_30%_70%,rgba(139,92,246,0.3),transparent_50%)',
                'radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.3),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.3),transparent_50%)'
              ]
            }}
            transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(-45deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:60px_60px]"></div>
        </div>
        
        <div className="container relative z-10">
          <motion.div 
            className="max-w-5xl mx-auto text-center text-white"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-6 py-3 mb-8">
              <FaRocket className="text-blue-400 mr-2" />
              <span className="text-white font-semibold">Ready to Start?</span>
            </div>

            <h2 className="mb-8 text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              Ready to Be Our 
              <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Next Success Story?
              </span>
            </h2>
            
            <p className="mb-12 text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
              Contact us today to discuss your cloud transformation needs and discover how we can help 
              you achieve similar remarkable results for your business.
            </p>
            
            <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <Link 
                  to="/contact" 
                  className="group inline-flex items-center px-12 py-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-2xl shadow-2xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 text-lg"
                >
                  <span>Start Your Journey</span>
                  <FaArrowRight className="ml-3 transform group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
              </motion.div>
              
              <div className="text-center lg:text-left">
                <p className="text-blue-200 mb-2">Or reach out directly:</p>
                <div className="flex flex-col lg:flex-row items-center gap-6 text-lg font-semibold">
                  <motion.a 
                    href="mailto:contact@dhyontechnologies.com" 
                    className="text-white/90 hover:text-white transition-colors duration-200 flex items-center group"
                    whileHover={{ scale: 1.05 }}
                  >
                    <FaUsers className="mr-2 group-hover:scale-110 transition-transform duration-200" />
                    contact@dhyontechnologies.com
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default CaseStudiesPage;